import React, { useCallback, useContext, useEffect } from "react";
import { Fragment, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react'
import Modal from "../../components/Modal";
import { Link } from "react-router-dom";
import {
    CalendarIcon,
    ChartBarIcon,
    Bars3Icon,
    BuildingOfficeIcon,
    UserCircleIcon,
    UserGroupIcon,
    CheckCircleIcon,
    UserIcon,
    ClockIcon,
    BeakerIcon,
    AcademicCapIcon,
    MapPinIcon,
    DocumentIcon,
    ExclamationCircleIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline'
import { SearchIcon, MapPinIcon as SolidMapPinIcon, CakeIcon, BuildingOfficeIcon as SolidBuildingOfficeIcon } from '@heroicons/react/24/solid'
import api from "../../api";
import Sidebar from "../../components/Sidebar";
import Breadcrumbs from "../../components/Breadcrumbs";
import SimpleCombobox from "../../components/SimpleCombobox";
import Checkbox from "../../components/Checkbox";
import InlineCheckbox from "../../components/InlineCheckbox"
import TextEditor from "../../components/TextEditor";
import debounce from "just-debounce-it";
import toast, { Toaster } from "react-hot-toast";
import { formatDateDDMMYYYY } from "../../util/formatDate";

const actions = [
    {
        name: 'Nueva consulta',
        background: "bg-green-100",
        href: "/new",
        icon: <CheckCircleIcon className="h-6 w-6 text-green-600" />,
    },
    {
        name: 'Datos del paciente',
        background: "bg-purple-200",
        href: "/edit",
        icon: <UserIcon className="h-6 w-6 text-purple-700" />
    },
    {
        name: 'Historial de consultas',
        background: "bg-blue-200",
        href: "/history",
        icon: <ClockIcon className="h-6 w-6 text-blue-700" />
    },
    {
        name: 'Documentos adjuntos',
        background: "bg-green-100",
        href: "/documents",
        icon: <DocumentIcon className="h-6 w-6 text-[#728D57]" />
    },
    {
        name: 'Informes',
        background: "bg-gray-200",
        href: "/reports",
        icon: <DocumentTextIcon className="h-6 w-6 text-[#747170]" />
    },
]

const pages = [
    { name: 'Pacientes', href: '/patients', current: false },
    { name: 'Detalle  del paciente', href: `#`, current: true },
]

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function PatientDetailPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [cities, setCities] = useState([])
    const [patient, setPatient] = useState()
    const [doctors, setDoctors] = useState([])
    const [doctor, setDoctor] = useState(undefined)
    const [isUrgent, setIsUrgent] = useState(false)
    const [openConfirmation, setOpenConfirmation] = useState(false)
    const [openSelect, setOpenSelect] = useState(false)
    const [openSuccess, setOpenSuccess] = useState(false)
    const [error, setError] = useState('');
    const [errorMessage, setErrorMessage] = useState('')
    const [loading, setLoading] = useState(true)
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        observations: "",
        procedures: "",
    });
    const { idPatient } = useParams()

    const notify = (message, type = "success") => {
        toast(message, {
            style: {
                background: type === "success" ? "#4caf50" : "#f44336",
                color: "#fff",
                padding: "16px",
                borderRadius: "8px",
            },
            duration: 4000,
        });
    };
        

    useEffect(() => {
        api.insurances.getMulti().then((res) =>{
            api.cities.getMulti().then((resp) => {
                setCities(resp);
                api.patients.getOne(idPatient).then((response) => {
                    console.log(response)
                    setPatient({ ...response, name: response.firstName + " " + response.lastName, city: resp.find((city) => city.id === (response.city))?.name, insurance: res.find((insurance) => insurance.id === (response.insurance))?.name });
                    setFormData({
                        observations: response.observations,
                        procedures: response.procedures,
                    })
                    setOpen(!response.confirmedData);
                    setLoading(false);
                })
            })
        })
        api.users.getDoctors().then((response) => {
            setDoctors(response.map((element) => {
                const isLicenciado = element.role.toLowerCase() === "licenciado";
                const prefix = isLicenciado 
                    ? "Lic."
                    : element.sex.toLowerCase() === "masculino"
                        ? "Dr."
                        : "Dra.";
                return {
                    name: `${prefix} ${element.firstName} ${element.lastName}`,
                    ...element
                };
            }));
        });
    }, [])

    const debouncedPATCHData = useCallback(
    debounce(async (data) => {
        try {
            await api.patients.update(idPatient, data, true);
            notify("Datos actualizados correctamente", "success");
        } catch (error) {
            console.error(error);
            const message =
                error?.response?.data?.detail ||
                "Ocurrió un error al actualizar los datos";
            notify(message, "error");
        }
    }, 1000),
    []
);

    const handleChange = (name, value) => {
        setFormData((prev) => {
            const newData = { ...prev, [name]: value };
            debouncedPATCHData(newData);
            return newData;
        });
    };

    const handleDoctor = (field, value) => {
        setDoctor(value)
    }

    const handleIsUrgent = (field, value) => {
        setIsUrgent(value)
    }

    const handleSubmit = () => {
        api.turns.create({ doctorGovId: doctor.govId, patientGovId: patient.govId, isUrgent: isUrgent }).then(() => (setOpenConfirmation(false), setOpenSuccess(true))).catch((error) => error.code === 'serverError' ? (setErrorMessage('Error 500: Error del servidor'), setLoading(false)) : (setErrorMessage(error?.message), setLoading(false)))
    }

    useEffect(() => {
        if (!openConfirmation)
            setErrorMessage('')
    }, [openConfirmation])

    useEffect(() => {
        if (!openSelect)
            setError('')
    }, [openSelect])

    function formatManagementPlans(plans) {
    return plans
        .map((plan) => `${plan.date}: ${plan.description}`)
        .join("<br/>");
    }

    const validation = (
        <>
            < div >
                <div className="text-center">
                    <Dialog.Title as="h3" className="text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                        Validacion de datos
                    </Dialog.Title>
                    <div className="mt-2 w-full">
                        <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-400">
                            Es necesario validar los datos del paciente para poder continuar
                        </label>
                        <div className="relative w-2/3 rounded-md mx-auto shadow-sm">
                        </div>
                    </div>
                </div>
            </div >
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={() => navigate(`/patients/${idPatient}/edit`)}
                >
                    Continuar
                </button>
                <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => navigate('/patients')}
                >
                    Volver
                </button>
            </div>
        </>
    )

    const confirmation = (
        <>
            < div >
                <div className="text-center">
                    <Dialog.Title as="h3" className="text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                        Confirmación
                    </Dialog.Title>
                    <div className="mt-2 w-full">
                        <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-400">
                            ¿Está seguro de que quiere agregar el paciente al turnero?
                        </label>
                        <div className="relative w-2/3 rounded-md mx-auto">
                            {errorMessage && Object.keys(errorMessage).map((element) => (<p className="mt-2 text-sm text-red-600">{errorMessage[element]}</p>))}
                        </div>
                    </div>
                </div>
            </div >
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={() => handleSubmit()}
                >
                    Aceptar
                </button>
                <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => (setOpenConfirmation(false), setOpenSelect(true))}
                >
                    Regresar
                </button>
            </div>
        </>
    )

    const selectDoctor = (
        <>
            < div >
                <div className="text-center">
                    <Dialog.Title as="h3" className="text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                        Agregar al turnero
                    </Dialog.Title>
                    <div className="mt-2 w-full">
                        <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-400">
                            Seleccione el doctor que atenderá al paciente
                        </label>
                        <div className="flex gap-3 relative rounded-md mx-auto mt-3">
                            <SimpleCombobox value={doctor} containerClassName='w-2/3 shadow-sm' handleChange={handleDoctor} name="doctor" options={doctors} />
                            <InlineCheckbox containerClassName='relative flex pr-2 items-start mt-3' className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded" value={isUrgent} handleChange={handleIsUrgent} name='isUrgent' label='Es Urgencia' />
                        </div>
                        {error && <p className="mt-2 text-sm text-red-600">
                            {error}
                        </p>}
                    </div>
                </div>
            </div >
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={() => doctor ? (setOpenSelect(false), setOpenConfirmation(true)) : setError('Debe seleccionar un doctor para continuar')}
                >
                    Aceptar
                </button>
                <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => (setDoctor(undefined), setOpenSelect(false))}
                >
                    Cancelar
                </button>
            </div>
        </>
    )

    const success = (
        <>
            < div >
                <div className="text-center">
                    <Dialog.Title as="h3" className="text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                        Paciente agregado
                    </Dialog.Title>
                    <div className="mt-2 w-full">
                        <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-400">
                            El paciente fue agregado exitosamente al turnero.
                        </label>
                        <div className="relative w-2/3 rounded-md mx-auto shadow-sm">
                        </div>
                    </div>
                </div>
            </div >
            <div className="mt-5 sm:mt-6 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={() => setOpenSuccess(false)}
                >
                    Aceptar
                </button>
            </div>
        </>
    )

    return (
        <div className="bg-gray-50 dark:bg-gray-800 h-screen text-black dark:text-white">
            <Sidebar currentScreen={'Pacientes'} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            {loading ?
                <div className="flex h-full items-center justify-center ">
                    <div className="w-16 h-16 border-b-2 border-blue-900 dark:border-white rounded-full animate-spin"></div>
                </div> :
                <div className="md:pl-64 flex flex-col bg-gray-100 dark:bg-gray-900 flex-1">
                    <Modal size='sm:max-w-lg w-full' content={validation} open={open} setOpen={setOpen} />
                    <Modal size='sm:max-w-lg w-full' content={confirmation} open={openConfirmation} setOpen={setOpenConfirmation} />
                    <Modal size='sm:max-w-lg w-full' content={selectDoctor} open={openSelect} setOpen={setOpenSelect} />
                    <Modal size='sm:max-w-lg w-full' content={success} open={openSuccess} setOpen={setOpenSuccess} />
                    <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-100 dark:bg-gray-900">
                        <button
                            type="button"
                            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                            onClick={() => setSidebarOpen(true)}
                        >
                            <span className="sr-only">Open sidebar</span>
                            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                        </button>
                    </div>

                    <div className="flex flex-col flex-1 pb-4 ml-6 mt-6">
                        <Breadcrumbs pages={pages} />
                        <div className="ml-4">
                            <h1 className="mt-6 text-3xl font-bold leading-9 text-gray-900 dark:text-white">{patient?.name}</h1>
                            <div className="flex flex-col sm:flex-row sm:justify-start flex-wrap sm:items-center mt-2 gap-y-1">
                                <div>
                                    <span className="text-sm leading-5 font-medium text-gray-500">C.I. {patient?.govId}</span>
                                </div>
                                <div className="sm:ml-6">
                                    <CakeIcon className="inline-block h-5 w-5 text-gray-500" />
                                    <span className="text-sm leading-5 ml-2 font-medium text-gray-500">
                                        {patient?.age ? `${patient.age} años` : "---"} 
                                    </span>
                                </div>
                                {patient.city ?
                                    <div className="sm:ml-6">
                                        <SolidMapPinIcon className="inline-block h-5 w-5 text-gray-500" />
                                        <span className="text-sm leading-5 ml-2 font-medium text-gray-500">
                                            {patient.city}
                                        </span>
                                    </div>
                                    : null
                                }
                                {patient.insurance ?
                                    <div className="sm:ml-6">
                                        <span className="text-sm leading-5 ml-2 font-medium text-gray-500">
                                            <b>Seguro:</b> {patient.insurance}
                                        </span>
                                    </div>
                                    : null
                                }
                                <div className="sm:ml-6">
                                    <span className=" hidden sm:inline text-sm leading-5font-medium text-gray-500">| </span>
                                    <span className="text-sm leading-5 sm:ml-4 font-medium text-gray-500">Última Visita: {patient?.lastAppointmentDate ? formatDateDDMMYYYY(patient.lastAppointmentDate) : '--'} </span>
                                </div>
                                <div className="sm:ml-6">
                                    <span className=" hidden sm:inline text-sm leading-5font-medium text-gray-500">| </span>
                                    <span className="text-sm leading-5 sm:ml-4 font-medium text-gray-500">Última Consulta: {patient?.lastConsultationDate ? formatDateDDMMYYYY(patient.lastConsultationDate) : '--'} </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <main className="flex-1 bg-gray-50 dark:bg-gray-800">
                        { (!/^\d+$/.test(patient.govId) || !patient.govId) &&
                            <Link to={`/patients/${idPatient}/edit`} className="flex  gap-1 w-full justify-center items-center px-10 py-5 bg-yellow-100 text-yellow-800">
                                <ExclamationCircleIcon className="h-6 w-6"/>
                                <p>Actualice el número de cédula del paciente</p>
                            </Link>
                        }
                        <div className="flex flex-col md:flex-row w-full justify-between px-8 pt-16">
                            <div className="flex flex-col gap-4 w-full">
                                {actions.map((action) => (
                                    <div
                                        key={action.name}
                                        className="relative w-full mx-auto rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-5 shadow-sm flex items-center gap-x-3 hover:border-gray-400"
                                    >
                                        <div className={`flex-shrink-0 rounded p-2 ${action.background}`}>
                                            {action.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <Link to={`/patients/${idPatient}${action.href}`} className="focus:outline-none">
                                                <span className="absolute inset-0" aria-hidden="true" />
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">{action.name}</p>
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                                <div
                                    key='Agregar paciente al turnero'
                                    className="relative w-full mx-auto rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900  px-6 py-5 shadow-sm flex items-center gap-x-3 hover:border-gray-400"
                                >
                                    <div className='flex-shrink-0 rounded p-2 bg-red-100'>
                                        <DocumentIcon className="h-6 w-6 text-[#bb4c4c]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <button onClick={() => setOpenSelect(true)} className="focus:outline-none">
                                            <span className="absolute inset-0" aria-hidden="true" />
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">Agregar paciente al turnero</p>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="w-full flex flex-col justify-around mt-4 md:mt-0">
                                <TextEditor 
                                    name="observations"
                                    label="Observaciones:"
                                    value={formData.observations}
                                    handleChange={handleChange}
                                    error={null}
                                    required={false}
                                    containerClassName="px-4 pb-10"
                                    readOnly={false}
                                    height={160}
                                />
                                <TextEditor 
                                    name="procedures"
                                    label="Procedimientos:"
                                    value={formData.procedures}
                                    handleChange={handleChange}
                                    error={null}
                                    required={false}
                                    containerClassName="px-4 pb-10"
                                    height={160}
                                />
                            </div>
                        </div>
                        <div className="px-8 mt-5">
                            <TextEditor 
                                name="managementPlans"
                                label="Historial de conductas:"
                                value={formatManagementPlans(patient.managementPlans)}
                                handleChange={() => {}}
                                error={null}
                                required={false}
                                containerClassName="px-4"
                                readOnly={true}
                                    />
                        </div>
                    </main>
                </div>
            }
            <Toaster />
        </div >
    );
}
